// ./src/components/features/Travels/TravelWaypointsDrawer/WaypointsMapView.tsx
import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { Box, Center, Loader, Text, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Expense } from "@/utils/api";
import { useGoogleMapsDisplay } from "@/contexts/GoogleMapsContext/GoogleMapsContext";
import { AppIcon } from "@/components/generic";
import { EXPENSE_TYPES } from "@/config";

import {
  getExpenseCoordinates,
  getExpensesWithCoordinates,
  getMarkerConfig,
  calculateBounds,
  padBounds,
  decodePolyline,
  ROUTE_LINE_STYLE,
  DEFAULT_MAP_OPTIONS,
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
} from "./waypointsMapUtils";

// ============================================
// Types
// ============================================

export interface WaypointsMapViewProps {
  /** All expenses/waypoints to display on the map */
  expenses: Expense[];

  /** Currently hovered waypoint ID (from list) */
  hoveredWaypointId: string | null;

  /** Callback when a marker is hovered */
  onMarkerHover: (waypointId: string | null) => void;

  /** Callback when a marker is clicked */
  onMarkerClick?: (waypoint: Expense) => void;

  /** Height of the map container */
  height?: string | number;

  /** Distance unit label for info windows */
  distanceLabel?: string;

  /** Optional Google Encoded Polyline for the driven GPS route */
  encodedPolyline?: string | null;
}

// ============================================
// Main Component
// ============================================

const WaypointsMapViewComponent: React.FC<WaypointsMapViewProps> = ({
  expenses,
  hoveredWaypointId,
  onMarkerHover,
  onMarkerClick,
  height = "100%",
  encodedPolyline,
}) => {
  const { t } = useTranslation(["travels", "common"]);
  const { libraries, isLoaded, isLoading, error } = useGoogleMapsDisplay();

  // Refs
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  // Derived data - use useMemo instead of useState+useEffect
  const waypointsWithCoords = useMemo(() => {
    return getExpensesWithCoordinates(expenses);
  }, [expenses]);

  // ============================================
  // Initialize Map
  // ============================================

  useEffect(() => {
    if (!isLoaded || !libraries?.maps || !libraries?.marker || !mapContainerRef.current) {
      console.log("[WaypointsMapView] Waiting for dependencies:", {
        isLoaded,
        hasMapsLib: !!libraries?.maps,
        hasMarkerLib: !!libraries?.marker,
        hasContainer: !!mapContainerRef.current,
      });
      return;
    }
    if (mapRef.current) {
      console.log("[WaypointsMapView] Map already initialized");
      return;
    }

    console.log("[WaypointsMapView] Initializing map...");

    const { Map } = libraries.maps;

    // Calculate initial bounds
    const bounds = calculateBounds(waypointsWithCoords);
    const paddedBounds = bounds ? padBounds(bounds) : null;

    try {
      // Create map - uses Google's built-in map type control
      const map = new Map(mapContainerRef.current, {
        ...DEFAULT_MAP_OPTIONS,
        center: paddedBounds
          ? {
            lat: (paddedBounds.north + paddedBounds.south) / 2,
            lng: (paddedBounds.east + paddedBounds.west) / 2,
          }
          : DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
      });

      console.log("[WaypointsMapView] Map created successfully");

      // Fit bounds if we have waypoints
      // Note: LatLngBounds is on the global google.maps object, not on libraries.maps
      if (paddedBounds && google?.maps?.LatLngBounds) {
        const googleBounds = new google.maps.LatLngBounds(
          { lat: paddedBounds.south, lng: paddedBounds.west },
          { lat: paddedBounds.north, lng: paddedBounds.east }
        );
        map.fitBounds(googleBounds);
      }

      // Create info window for hover/click
      if (google?.maps?.InfoWindow) {
        infoWindowRef.current = new google.maps.InfoWindow({
          disableAutoPan: true,
        });
      }

      mapRef.current = map;
    } catch (error) {
      console.error("[WaypointsMapView] Error creating map:", error);
    }
  }, [isLoaded, libraries, waypointsWithCoords]);

  // ============================================
  // Create/Update Markers
  // ============================================

  useEffect(() => {
    if (!isLoaded || !mapRef.current) {
      console.log("[WaypointsMapView] Markers: waiting for dependencies");
      return;
    }

    // Check if google.maps.Marker is available
    if (!google?.maps?.Marker) {
      console.error("[WaypointsMapView] google.maps.Marker not available");
      return;
    }

    console.log("[WaypointsMapView] Creating markers, waypointsWithCoords:", waypointsWithCoords.length);

    const map = mapRef.current;

    // Clear existing markers
    markersRef.current.forEach((marker) => {
      marker.setMap(null);
    });
    markersRef.current.clear();

    // Filter to only waypoints (travel points) for numbering
    const travelPoints = waypointsWithCoords.filter(
      (w) => w.expense.expenseType === EXPENSE_TYPES.TRAVEL_POINT
    );

    console.log("[WaypointsMapView] Travel points:", travelPoints.length);

    // Create markers for all waypoints with coordinates
    waypointsWithCoords.forEach((waypointData) => {
      const { expense } = waypointData;
      const coords = getExpenseCoordinates(expense);
      if (!coords || !expense.id) {
        console.log("[WaypointsMapView] Skipping waypoint - no coords or id:", expense.id, coords);
        return;
      }

      console.log("[WaypointsMapView] Creating marker for:", expense.id, coords);

      // Find index within travel points for numbering
      const travelPointIndex = travelPoints.findIndex((tp) => tp.expense.id === expense.id);
      const isWaypoint = travelPointIndex !== -1;
      const displayIndex = isWaypoint ? travelPointIndex : waypointData.index;
      const total = isWaypoint ? travelPoints.length : waypointsWithCoords.length;

      // Get marker configuration
      const config = getMarkerConfig(expense, displayIndex, total, isWaypoint);

      try {
        // Create regular marker with custom icon (highlighting is handled by separate effect)
        const marker = new google.maps.Marker({
          map,
          position: coords,
          title: expense.whereDone || expense.location || `Point ${displayIndex + 1}`,
          label: {
            text: config.label,
            color: config.glyphColor,
            fontSize: "12px",
            fontWeight: "bold",
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: config.color,
            fillOpacity: 1,
            strokeColor: config.color,
            strokeWeight: 2,
            scale: 14,
          },
          zIndex: displayIndex,
        });

        console.log("[WaypointsMapView] Marker created successfully:", expense.id);

        // Add event listeners
        marker.addListener("click", () => {
          onMarkerClick?.(expense);
        });

        marker.addListener("mouseover", () => {
          onMarkerHover(expense.id || null);
        });

        marker.addListener("mouseout", () => {
          onMarkerHover(null);
        });

        markersRef.current.set(expense.id, marker);
      } catch (error) {
        console.error("[WaypointsMapView] Error creating marker:", error);
      }
    });

    // Cleanup function
    return () => {
      markersRef.current.forEach((marker) => {
        marker.setMap(null);
      });
      markersRef.current.clear();
    };
  }, [isLoaded, waypointsWithCoords, onMarkerHover, onMarkerClick]);

  // ============================================
  // Update marker highlighting on hover
  // ============================================

  useEffect(() => {
    if (!isLoaded || !google?.maps?.SymbolPath) return;

    // Filter to only waypoints (travel points) for numbering
    const travelPoints = waypointsWithCoords.filter(
      (w) => w.expense.expenseType === EXPENSE_TYPES.TRAVEL_POINT
    );

    markersRef.current.forEach((marker, id) => {
      const waypointData = waypointsWithCoords.find((w) => w.expense.id === id);
      if (!waypointData) return;

      const { expense } = waypointData;
      const travelPointIndex = travelPoints.findIndex((tp) => tp.expense.id === expense.id);
      const isWaypoint = travelPointIndex !== -1;
      const displayIndex = isWaypoint ? travelPointIndex : waypointData.index;
      const total = isWaypoint ? travelPoints.length : waypointsWithCoords.length;

      const config = getMarkerConfig(expense, displayIndex, total, isWaypoint);
      const isHighlighted = id === hoveredWaypointId;

      // Update marker icon and label for highlighting
      marker.setIcon({
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: config.color,
        fillOpacity: 1,
        strokeColor: isHighlighted ? "#ffffff" : config.color,
        strokeWeight: isHighlighted ? 3 : 2,
        scale: isHighlighted ? 18 : 14,
      });
      marker.setLabel({
        text: config.label,
        color: config.glyphColor,
        fontSize: isHighlighted ? "14px" : "12px",
        fontWeight: "bold",
      });
      marker.setZIndex(isHighlighted ? 1000 : displayIndex);
    });
  }, [hoveredWaypointId, isLoaded, waypointsWithCoords]);

  // ============================================
  // Create/Update Route Polyline
  // ============================================

  // ============================================
  // Create/Update Route Polyline (from encodedPolyline)
  // ============================================

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    // Remove existing polyline
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    // Only draw route if encodedPolyline is provided
    if (!encodedPolyline || !google?.maps?.Polyline) return;

    const path: any[] = decodePolyline(encodedPolyline);
    if (path.length < 2) return;

    const polyline = new google.maps.Polyline({
      path,
      ...ROUTE_LINE_STYLE,
      map: mapRef.current,
    });

    polylineRef.current = polyline;

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [isLoaded, encodedPolyline]);

  // ============================================
  // Fit bounds when waypoints change
  // ============================================

  const fitBounds = useCallback(() => {
    if (!mapRef.current || !google?.maps?.LatLngBounds || waypointsWithCoords.length === 0) return;

    const bounds = calculateBounds(waypointsWithCoords);
    if (!bounds) return;

    const paddedBounds = padBounds(bounds);
    const googleBounds = new google.maps.LatLngBounds(
      { lat: paddedBounds.south, lng: paddedBounds.west },
      { lat: paddedBounds.north, lng: paddedBounds.east }
    );

    mapRef.current.fitBounds(googleBounds);
  }, [waypointsWithCoords]);

  // Fit bounds when waypoints change
  useEffect(() => {
    if (isLoaded && mapRef.current) {
      fitBounds();
    }
  }, [isLoaded, fitBounds]);

  // ============================================
  // Render
  // ============================================

  // Loading state
  if (isLoading) {
    return (
      <Center h={height}>
        <Loader size="lg" />
      </Center>
    );
  }

  // Error state
  if (error) {
    return (
      <Center h={height}>
        <Stack align="center" gap="sm">
          <AppIcon iconName="MapOff" size={48} color="var(--mantine-color-red-5)" />
          <Text c="dimmed" ta="center">
            {t("travels:waypoints.map.loadError")}
          </Text>
        </Stack>
      </Center>
    );
  }

  // No waypoints with coordinates
  if (waypointsWithCoords.length === 0) {
    return (
      <Center h={height}>
        <Stack align="center" gap="sm">
          <AppIcon iconName="MapPinOff" size={48} color="var(--mantine-color-gray-5)" />
          <Text c="dimmed" ta="center">
            {t("travels:waypoints.map.noCoordinates")}
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Box pos="relative" h={height}>
      {/* Map Container - Google Maps provides its own map type control */}
      <Box
        ref={mapContainerRef}
        w="100%"
        h="100%"
        style={{ borderRadius: "var(--mantine-radius-md)" }}
      />
    </Box>
  );
};

export const WaypointsMapView = React.memo(WaypointsMapViewComponent);